import {
  IonApp,
  IonCol,
  IonContent,
  IonPage,
  IonSpinner,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
} from "@ionic/react";
import Cross from "assets/images/icon/svg/Cross";
import BackButtonTitle from "components/mobile-components/Common/BackButtonTitle";
import { COUNTRYINFO } from "data/country";
import { dateTimeFormatter, numberWithCommas } from "helpers/helper";
import moment from "moment";
import React, { useEffect, useRef, useState } from "react";
import { Bar, getElementAtEvent } from "react-chartjs-2";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedDate } from "redux/container/dashboardSlice";
import Accounts from "services/Accounts";

const TotalEmployeesPage = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [employeeData, setEmployeeData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [pageCount, setPageCount] = useState(2);
  const { createUserData } = useSelector((state) => state.user);
  const ekkLanguage = useSelector((state) => state.lan.lan);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [currency, setCurrency] = useState("");
  const [totalTableAmount, setTotalTableAmount] = useState(0);
  const dateFormat = useSelector((state) => state.date.format);
  const [getDate, setGetDate] = useState({
    start: moment().startOf("month").format("YYYY-MM-DD"),
    end: moment().endOf("month").format("YYYY-MM-DD"),
  });
  const [filterClicked, setFilterClicked] = useState(false);
  const barRef = useRef();
  const chartData = {
    labels: data?.map((item) =>
      moment(item?.month, "MM-YYYY").format("MMM/YY")
    ),
    datasets: [
      {
        data: data?.map((item) => item?.total_salary),
        backgroundColor: "#FF3B30",
      },
    ],
  };
  const chartOptions = {
    barPercentage: 0.7,
    plugins: {
      legend: false,
    },
  };
  const getEmployeeTableData = async (start, end, timezone) => {
    setLoading(true);
    let response = await Accounts.getEmployeeCardWithFilter(
      start,
      end,
      timezone
    );
    if (response.status === 200) {
      setLoading(false);
      setEmployeeData(response.results);
      setCurrency(response.currency);
      setTotalTableAmount(response.all_emp_total_salary);
    } else {
      setLoading(false);
    }
  };
  useEffect(() => {
    getEmployeeTableData(getDate.start, getDate.end, timeZone);
  }, [getDate.start, getDate.end, timeZone]);

  let loadMoreData = async () => {
    if (hasMoreData) {
      let response = await Accounts.getEmployeeCard(timeZone);
      if (response.status === 200) {
        setLoading(false);
        setPageCount((prev) => prev + 1);
        setEmployeeData([...employeeData, ...response.data]);
      } else {
        setHasMoreData(false);
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    const getChartData = async () => {
      setChartLoading(true);
      const response = await Accounts.getEmployeeChartDetails(timeZone);
      if (response.status === 200) {
        setData(response?.results);
      } else {
        setData([]);
      }
      setChartLoading(false);
    };

    getChartData();
  }, [timeZone]);

  const barClickHandler = async (event) => {
    const chartConfigs = {
      employee: {
        ref: barRef,
        data: chartData,
      },
    };
    const config = chartConfigs["employee"];
    if (!config) return;
    const elementIndex = getElementAtEvent(config.ref.current, event)[0]?.index;
    if (elementIndex === undefined) return;
    const date = config.data?.labels[elementIndex];
    const value = config.data?.datasets[0].data[elementIndex];
    if (value === 0) return;
    const startTime = moment(date, "MMM/YY")
      .startOf("month")
      .format("YYYY-MM-DD");
    const endTime = moment(date, "MMM/YY").endOf("month").format("YYYY-MM-DD");
    const dateTime = {
      start: startTime,
      end: endTime,
      level: "month",
    };
    dispatch(getSelectedDate(dateTime));
    setGetDate(dateTime);
    getEmployeeTableData(startTime, endTime, timeZone);
  };
  const handleCrossFilter = () => {
    let date = {
      value: "month",
      label: ekkLanguage.dashboard.thisMonth,
      name: "This Month",
    };
    let timeDate = dateTimeFormatter(date?.value);
    let dateTime = { ...timeDate, level: date?.name };
    setGetDate({
      start: moment(timeDate?.start).format("YYYY-MM-DD"),
      end: moment(timeDate?.end).format("YYYY-MM-DD"),
    });
    dispatch(getSelectedDate(dateTime));
    setFilterClicked(false);
  };

  return (
    <IonApp>
      <IonPage>
        <IonContent>
          <BackButtonTitle
            title="Employees"
            filter={true}
            setGetDate={setGetDate}
            setFilterClicked={setFilterClicked}
            filterClicked={filterClicked}
          />
          <section className="pt-14 px-4">
            {!filterClicked && (
              <div className="chart-wrapper relative mb-4">
                {chartLoading && (
                  <div className="absolute h-full w-full flex justify-center items-center left-[7%] top-[5%]">
                    <IonSpinner name="circular" />
                  </div>
                )}
                <div className="px-2 pb-1 flex justify-between items-center pt-5">
                  <h4 className="font-bold text-18 sm:text-20 text-black-1000 mb-2">
                    Total Employee Salary
                  </h4>
                </div>
                <div
                  className="flex justify-center px-2"
                  style={{ width: "100%", maxWidth: "768px" }}
                >
                  <Bar
                    data={chartData}
                    options={chartOptions}
                    ref={barRef}
                    onClick={barClickHandler}
                  />
                </div>
              </div>
            )}

            <div className={`pb-10 ${!filterClicked ? "pt-10" : "pt-5"}`}>
              <div>
                <div className="flex items-center">
                  <h4 className="font-bold text-16 text-black-1000">
                    Total Salary on{" "}
                    {moment(getDate?.start, "YYYY-MM-DD").format(dateFormat)} -{" "}
                    {moment(getDate?.end, "YYYY-MM-DD").format(dateFormat)}
                  </h4>
                  {filterClicked && (
                    <div
                      onClick={handleCrossFilter}
                      className="ml-1 p-1 bg-white border rounded-full shadow-xl cursor-pointer"
                    >
                      <Cross color={"black"} />
                    </div>
                  )}
                </div>
                <h4 className="font-bold text-16 text-black-1000 mb-4">
                  Total Amount: {currency}
                  {numberWithCommas(totalTableAmount)}
                </h4>
                <div>
                  <table
                    className="table-auto w-full bg-white"
                    style={{
                      boxShadow: "0px 3px 20px 0px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    <thead>
                      <tr className="border h-[45px] border-r-0 border-l-0 border-D9D9D9 px-4">
                        <th className="border p-1 font-bold text-12">
                          Employee
                        </th>
                        <th className="border p-1 font-bold text-14">
                          Working Hours
                        </th>
                        <th className="border p-1 font-bold text-14">
                          Salary Rate
                        </th>
                        <th className="border p-1 font-bold text-14">Salary</th>
                        <th className="border p-1 font-bold text-14">
                          Salary Payout Amount
                        </th>
                        <th className="border p-1 font-bold text-14">
                          Approved Leaves
                        </th>{" "}
                      </tr>
                    </thead>
                    <tbody>
                      {employeeData?.length
                        ? employeeData?.map((el, i) => {
                            return (
                              <tr className="text-center h-[34px] border border-r-0 border-l-0 border-D9D9D9 px-4 cursor-pointer">
                                {/* <td className='border p-2 w-14 font-semibold text-10'>
                                                        {digitChanger(i + 1)}
                                                    </td> */}
                                <td className="border font-semibold p-2 text-14 text-left">
                                  <p>{el?.name}</p>
                                  <p>{el?.phone_number}</p>
                                </td>

                                <td className="border font-semibold p-2 text-14">
                                  {el?.hours_worked}
                                </td>
                                <td className="border font-semibold p-2 text-14">
                                  {currency}
                                  {el?.salary_rate}
                                </td>
                                <td className="border font-semibold p-2 text-14">
                                  {currency}
                                  {Number(el?.total_salary).toFixed(2)}
                                </td>
                                <td className="border font-semibold p-2 text-14">
                                  {currency}
                                  {Number(el?.salary_payout_amount).toFixed(2)}
                                </td>
                                <td className="border font-semibold p-2 text-14">
                                  {el?.leaves}
                                </td>
                              </tr>
                            );
                          })
                        : ""}
                    </tbody>
                  </table>
                  {loading ? (
                    <div className="flex items-center justify-center h-[156px] m-auto">
                      <IonSpinner name="crescent"></IonSpinner>
                    </div>
                  ) : (
                    ""
                  )}
                  {employeeData?.length === 0 && (
                    <>
                      <IonCol size="2"></IonCol>
                      <IonCol size="8">
                        <div
                          className="text-center flex items-center justify-center"
                          style={{
                            height: "calc(100vh - 420px)",
                          }}
                        >
                          <div>
                            <img
                              className="m-auto mb-[10px] mt-5"
                              src="/assets/images/not-found-file.png"
                              alt="emptyCard"
                            />
                            <p className="text-16 font-normal text-222222 mb-[35px]">
                              {ekkLanguage.orderList.notFound}
                            </p>
                          </div>
                        </div>
                      </IonCol>
                    </>
                  )}
                </div>
              </div>
            </div>
          </section>
          {/* <IonInfiniteScroll
            onIonInfinite={(ev) => {
              if (reachedEnd) {
                ev.target.complete();
                return;
              }
              loadMoreData();
              setTimeout(() => {
                ev.target.complete();
                if (!hasMoreData) {
                  setReachedEnd(true);
                }
              }, 1000);
            }}
          >
            <IonInfiniteScrollContent
              loadingText={ekkLanguage.orderList.loader}
              loadingSpinner={hasMoreData ? "bubbles" : null}
            ></IonInfiniteScrollContent>
          </IonInfiniteScroll> */}
        </IonContent>
      </IonPage>
    </IonApp>
  );
};

export default TotalEmployeesPage;
